/*
  Single Product section: change gallery image on swatch click.
  Scoped to sections with id="single-product-*".

  IMPORTANT: This copies the product-template idea:
  - rely on the theme’s existing `variantChange.ecomify.product` event
  - use variant.featured_media.position (fallback to featured_image/image.position)
  - navigate the Splide gallery by clicking the relevant thumbnail slide
*/

(() => {
  const getSectionRoots = (root = document) =>
    Array.from(root.querySelectorAll('[id^="single-product-"]'))

  function getSplideInstance (sectionRoot) {
    const mainSplideEl = sectionRoot.querySelector('.product-gallery .main-splide')
    if (!mainSplideEl) return null

    // Common expando properties (varies by build).
    if (mainSplideEl.splide && typeof mainSplideEl.splide.go === 'function') return mainSplideEl.splide
    if (mainSplideEl.__splide && typeof mainSplideEl.__splide.go === 'function') return mainSplideEl.__splide

    // Heuristic: scan element properties for an object that looks like Splide.
    // This avoids touching reusable init code.
    // eslint-disable-next-line guard-for-in
    for (const key in mainSplideEl) {
      const val = mainSplideEl[key]
      if (!val || typeof val !== 'object') continue
      if (typeof val.go !== 'function' || typeof val.mount !== 'function') continue
      if (val.root && val.root !== mainSplideEl) continue
      return val
    }

    return null
  }

  function goToIndex (sectionRoot, index) {
    const splide = getSplideInstance(sectionRoot)
    if (splide) {
      splide.go(index)
      return true
    }

    // Fallback: click thumb slide if we can't access the instance.
    const gallery = sectionRoot.querySelector('.product-gallery')
    if (!gallery) return false

    const thumbsSlide = gallery.querySelector(
      `.thumbs-splide .splide__slide:nth-child(${index + 1})`
    )

    if (!thumbsSlide) return false
    thumbsSlide.dispatchEvent(new MouseEvent('click', { bubbles: true }))
    return true
  }

  function isVariantMetafieldGallery (sectionRoot) {
    return Boolean(sectionRoot.querySelector('.product-gallery-variant-based'))
  }

  async function rerenderSectionGallery (sectionRoot, variantId) {
    const productHandle = sectionRoot?.dataset?.productHandle
    const productId = sectionRoot?.dataset?.productId
    if (!productHandle || !variantId) return false

    try {
      const url = `/products/${encodeURIComponent(productHandle)}?variant=${encodeURIComponent(String(variantId))}`
      const response = await fetch(url, { headers: { Accept: 'text/html' } })
      if (!response.ok) return false

      const html = await response.text()
      const newDocument = new DOMParser().parseFromString(html, 'text/html')
      const currentGallery = sectionRoot.querySelector('.product-gallery')
      const incomingGallery =
        (productId ? newDocument.getElementById(`product-gallery-${String(productId)}`) : null) ||
        newDocument.querySelector('.product-gallery')
      if (!currentGallery || !incomingGallery) return false

      currentGallery.replaceWith(incomingGallery)
      if (typeof window.initProductGallery === 'function') {
        window.initProductGallery()
      }

      return true
    } catch (err) {
      return false
    }
  }

  function getVariantMedia (variant) {
    return (
      variant?.featured_media ||
      variant?.featured_image ||
      variant?.image ||
      null
    )
  }

  function getVariantPosition (variant) {
    const media = getVariantMedia(variant)
    const pos = media?.position
    const num = Number(pos)
    return Number.isFinite(num) && num >= 1 ? num : null
  }

  function getVariantMediaId (variant) {
    const media = getVariantMedia(variant)
    if (!media) return null

    const id = media?.id ?? media?.media_id
    return id == null ? null : String(id)
  }

  function getIndexByMediaId (sectionRoot, mediaId) {
    if (!mediaId) return null

    const slides = Array.from(
      sectionRoot.querySelectorAll('.product-gallery .main-splide .splide__slide[data-media-id]')
    )

    if (!slides.length) return null

    const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId))
    return index >= 0 ? index : null
  }

  // Product-template style variant matching:
  // - build selectedOptions from all `.product-option` elements in the form
  // - find variant where `variant.options` matches those values
  function findSelectedVariantLikeProductTemplate (form) {
    if (!form?.dataset?.variants) return null

    const selectedOptions = []
    form.querySelectorAll('.product-option').forEach((elem) => {
      // match product.js behavior
      if (elem.type === 'radio') {
        if (elem.checked) selectedOptions.push(elem.value)
      } else {
        selectedOptions.push(elem.value)
      }
    })

    let variants
    try {
      variants = JSON.parse(form.dataset.variants)
    } catch (e) {
      return null
    }

    const key = JSON.stringify(selectedOptions)
    const byOptionsArray = variants.find((v) =>
      JSON.stringify(v.options) === key
    )

    // Quiet fallback for cases where `variant.options` is empty in JSON.
    const byOptionN = variants.find((v) => {
      const vals = [v.option1, v.option2, v.option3].filter((x) => x != null && x !== '')
      return JSON.stringify(vals) === key
    })

    return byOptionsArray || byOptionN || null
  }

  function syncSectionGalleryToVariant (sectionRoot, variant) {
    if (!variant) return

    const gallery = sectionRoot.querySelector('.product-gallery')
    const totalSlides = gallery?.querySelectorAll('.main-splide .splide__slide')?.length || 0
    if (!totalSlides) return

    const mediaId = getVariantMediaId(variant)
    const indexByMediaId = getIndexByMediaId(sectionRoot, mediaId)
    const position = getVariantPosition(variant)

    if (indexByMediaId == null && !position) return

    const index =
      indexByMediaId == null
        ? Math.max(0, Math.min(position - 1, totalSlides - 1))
        : indexByMediaId

    goToIndex(sectionRoot, index)
  }

  function getSelectedVariantForSection (sectionRoot) {
    const form = sectionRoot.querySelector('form[data-variants]')
    if (!form) return null

    return findSelectedVariantLikeProductTemplate(form)
  }

  function attachSectionHandlers (sectionRoot) {
    if (sectionRoot.dataset.spVariantInit === 'true') return

    const form = sectionRoot.querySelector('form[data-variants]')
    if (!form) return

    sectionRoot.dataset.spVariantInit = 'true'

    // Handle swatch/option changes within this section only.
    sectionRoot.addEventListener('change', (e) => {
      const target = e.target
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return
      if (!target.classList.contains('product-option')) return

      const selectedVariant = findSelectedVariantLikeProductTemplate(form)
      if (!selectedVariant) return

      // Keep the hidden variant id in sync for ATC.
      const idInput = form.querySelector('[name="id"]')
      if (idInput) idInput.value = selectedVariant.id

      // Update URL param like product template does.
      // This is scoped to the single-product section so it won't affect other components.
      try {
        const url = new URL(window.location.href)
        url.searchParams.set('variant', String(selectedVariant.id))
        window.history.replaceState({}, '', url)
      } catch (err) {
        // ignore
      }

      // Update gallery immediately.
      const isVariantGallery = isVariantMetafieldGallery(sectionRoot)
      if (isVariantGallery) {
        const selectedVariantId = String(selectedVariant.id)
        if (sectionRoot.dataset.spLastVariantId === selectedVariantId) return
        sectionRoot.dataset.spLastVariantId = selectedVariantId
        rerenderSectionGallery(sectionRoot, selectedVariant.id).then((ok) => {
          if (!ok) {
            syncSectionGalleryToVariant(sectionRoot, selectedVariant)
          }
        })
      } else {
        syncSectionGalleryToVariant(sectionRoot, selectedVariant)
      }
    })

    if (!isVariantMetafieldGallery(sectionRoot)) {
      syncSectionGalleryToVariant(sectionRoot, getSelectedVariantForSection(sectionRoot))
    }
  }

  function initSingleProductSections (root = document) {
    getSectionRoots(root).forEach(attachSectionHandlers)
  }

  function handleVariantChange (event) {
    const selectedVariant = event.detail
    if (!selectedVariant?.id) return

    getSectionRoots().forEach((sectionRoot) => {
      const form = sectionRoot.querySelector('form[data-variants]')
      const idInput = form?.querySelector('[name="id"]')

      if (!idInput || String(idInput.value) !== String(selectedVariant.id)) return
      const isVariantGallery = isVariantMetafieldGallery(sectionRoot)
      if (isVariantGallery) {
        if (sectionRoot.dataset.spLastVariantId === String(selectedVariant.id)) return
        sectionRoot.dataset.spLastVariantId = String(selectedVariant.id)
        rerenderSectionGallery(sectionRoot, selectedVariant.id).then((ok) => {
          if (!ok) {
            syncSectionGalleryToVariant(sectionRoot, selectedVariant)
          }
        })
      } else {
        syncSectionGalleryToVariant(sectionRoot, selectedVariant)
      }
    })
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSingleProductSections(event.target)
  })

  window.addEventListener('variantChange.ecomify.product', handleVariantChange)

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSingleProductSections()
    })
  } else {
    initSingleProductSections()
  }
})()
